@if (isVisible()) {
    <div
        class="tw-fixed tw-rounded-lg tw-shadow-md tw-bg-white tw-transition-colors tw-duration-300 tw-will-change-transform"
        [ngClass]="{
            'tw-border tw-border-amber-300': isActive(),
            locked: isLocked(),
            'tw-opacity-75 tw-pointer-events-none': getIsLoading(),
        }"
        [style.left.px]="isLocked() ? 0 : position().x"
        [style.top.px]="isLocked() ? 20 : position().y"
        [style.width.px]="size().width"
        [style.min-width.px]="280">
        <!-- Header with drag handle -->
        <div
            #dragHandle
            class="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-border-b tw-border-gray-200 tw-select-none tw-rounded-t-lg"
            [ngClass]="{
                'tw-cursor-move': !getIsLoading(),
                'tw-cursor-not-allowed': getIsLoading(),
                'tw-bg-amber-50': isActive(),
                'tw-bg-gray-50': !isActive(),
            }"
            (mousedown)="!getIsLoading() && onMouseDown($event, 'move')">
            <div class="tw-flex tw-items-center">
                <!-- Existing Icon -->
                <mat-icon
                    fontSet="material-symbols-outlined"
                    class="tw-mr-2 tw-text-gray-600"
                    >developer_mode</mat-icon
                >
                <!-- Title -->
                <span
                    class="tw-font-medium tw-text-gray-800 tw-flex tw-items-center">
                    <!-- Use flex to align spinner and text -->
                    Debug Console
                    <!-- Loading Spinner -->
                    @if (getIsLoading()) {
                        <mat-spinner
                            diameter="16"
                            class="tw-ml-2"
                            color="primary">
                        </mat-spinner>
                    }
                    <!-- Active Feature Count Badge -->
                    @if (activeFeatureCount() > 0 && !getIsLoading()) {
                        <span
                            class="tw-ml-1 tw-inline-flex tw-items-center tw-justify-center tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-rounded-full tw-bg-amber-100 tw-text-amber-800">
                            {{ activeFeatureCount() }} active
                        </span>
                    }
                </span>
            </div>
            <div class="tw-flex tw-items-center">
                <!-- Hide Button (New) -->
                <button
                    [disabled]="getIsLoading()"
                    mat-icon-button
                    type="button"
                    class="tw-text-gray-500 tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center hover:tw-bg-gray-100 tw-rounded-full disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    (click)="hideDebugUI(); $event.stopPropagation()"
                    [matTooltip]="'Hide Debug Console'">
                    <mat-icon
                        fontSet="material-symbols-outlined"
                        class="tw-text-gray-600">
                        visibility_off
                    </mat-icon>
                </button>
                <!-- Lock Button -->
                <button
                    [disabled]="getIsLoading()"
                    mat-icon-button
                    type="button"
                    class="tw-text-gray-500 tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center hover:tw-bg-gray-100 tw-rounded-full disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    (click)="toggleLocked(); $event.stopPropagation()"
                    [matTooltip]="
                        isLocked() ? 'Unlock Position' : 'Lock to Top Left'
                    ">
                    <mat-icon
                        fontSet="material-symbols-outlined"
                        [ngClass]="{
                            'lock-active': isLocked(),
                            'tw-text-gray-600': !isLocked(),
                        }">
                        {{ isLocked() ? 'lock' : 'lock_open' }}
                    </mat-icon>
                </button>
                <!-- Expand/Collapse Button -->
                <button
                    [disabled]="getIsLoading()"
                    mat-icon-button
                    type="button"
                    class="tw-text-gray-500 tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center hover:tw-bg-gray-100 tw-rounded-full disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    (click)="toggleExpanded(); $event.stopPropagation()"
                    [matTooltip]="isExpanded() ? 'Collapse' : 'Expand'">
                    <mat-icon
                        fontSet="material-symbols-outlined"
                        class="tw-text-gray-600"
                        >{{
                            isExpanded() ? 'expand_less' : 'expand_more'
                        }}</mat-icon
                    >
                </button>
            </div>
        </div>
        <!-- Environment toggle -->
        @if (isExpanded()) {
            <div>
                <div
                    class="tw-p-4 tw-flex tw-items-center tw-justify-between tw-border-b tw-border-gray-200">
                    <div class="tw-flex tw-items-center">
                        <mat-icon
                            fontSet="material-symbols-outlined"
                            class="tw-mr-3 tw-text-gray-600"
                            >tune</mat-icon
                        >
                        <span class="tw-text-sm tw-font-medium tw-text-gray-700"
                            >Environment</span
                        >
                    </div>
                    <div
                        class="tw-flex tw-items-center tw-bg-gray-100 tw-rounded-full tw-px-1 tw-py-1">
                        <mat-slide-toggle
                            [checked]="isActive()"
                            (change)="toggleDebugMode()"
                            [disabled]="getIsLoading()"
                            [color]="isActive() ? 'warn' : 'primary'"
                            matTooltip="Toggle between Production and Test environments">
                        </mat-slide-toggle>
                        <span
                            class="tw-ml-2 tw-mr-2 tw-text-sm tw-font-medium"
                            [ngClass]="{
                                'tw-text-amber-700': isActive(),
                                'tw-text-gray-600': !isActive(),
                            }">
                            {{ currentEnvironment() }}
                        </span>
                    </div>
                </div>
                <!-- Rest of the template remains the same -->
                <!-- Search input -->
                <div class="tw-p-4 tw-border-b tw-border-gray-200">
                    <div class="tw-relative">
                        <div
                            class="tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-3 tw-pointer-events-none">
                            <mat-icon
                                fontSet="material-symbols-outlined"
                                class="tw-text-gray-400"
                                >search</mat-icon
                            >
                        </div>
                        <input
                            type="text"
                            class="tw-w-full tw-pl-10 tw-pr-4 tw-py-2 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 disabled:tw-bg-gray-200 disabled:tw-cursor-not-allowed"
                            [ngModel]="searchQuery()"
                            (ngModelChange)="updateSearchQuery($event)"
                            [disabled]="getIsLoading()"
                            placeholder="Funktionen durchsuchen..." />
                    </div>
                </div>
                <!-- Search mode header -->
                @if (searchQuery()) {
                    <div
                        class="tw-p-2 tw-bg-blue-50 tw-border-b tw-border-blue-100">
                        <div class="tw-flex tw-items-center tw-justify-between">
                            <span class="tw-text-sm tw-text-blue-700"
                                >Suchergebnisse</span
                            >
                            <button
                                [disabled]="getIsLoading()"
                                mat-icon-button
                                class="tw-text-blue-500 tw-w-6 tw-h-6 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                                (click)="clearSearch()"
                                matTooltip="Clear search">
                                <mat-icon
                                    fontSet="material-symbols-outlined"
                                    class="tw-text-sm"
                                    >close</mat-icon
                                >
                            </button>
                        </div>
                    </div>
                }
                <!-- Material Tabs or Search Results -->
                @if (!searchQuery()) {
                    <div class="debug-console-tabs">
                        <mat-tab-group
                            [selectedIndex]="selectedTabIndex()"
                            (selectedIndexChange)="
                                !getIsLoading() && onTabChange($event)
                            "
                            animationDuration="0ms"
                            disableRipple>
                            @for (
                                tab of categoryTabs();
                                track tab;
                                let i = $index
                            ) {
                                <mat-tab
                                    [label]="tab"
                                    [disabled]="getIsLoading()">
                                    <!-- Put content directly in the tab template -->
                                    <div
                                        class="tw-max-h-[320px] tw-overflow-y-auto tw-bg-white">
                                        @for (
                                            category of getFilteredCategories(
                                                tab
                                            );
                                            track trackByCategory(
                                                $index,
                                                category
                                            )
                                        ) {
                                            <!-- Skip empty categories -->
                                            @if (category.features.length > 0) {
                                                <!-- Category header -->
                                                <div
                                                    class="tw-px-4 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-bg-gray-50 tw-sticky tw-top-0 tw-z-10">
                                                    {{ category.name }}
                                                </div>
                                                <div
                                                    class="tw-divide-y tw-divide-gray-100">
                                                    <!-- Use trackBy for performance -->
                                                    @for (
                                                        feature of category.features;
                                                        track trackByFeatureId(
                                                            $index,
                                                            feature
                                                        )
                                                    ) {
                                                        <div
                                                            class="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 hover:tw-bg-gray-50"
                                                            [class.tw-cursor-pointer]="
                                                                isActionFeature(
                                                                    feature
                                                                ) &&
                                                                !getIsLoading()
                                                            "
                                                            [class.tw-cursor-default]="
                                                                isToggleFeature(
                                                                    feature
                                                                ) ||
                                                                getIsLoading()
                                                            "
                                                            [class.tw-opacity-50]="
                                                                getIsLoading()
                                                            "
                                                            (click)="
                                                                isActionFeature(
                                                                    feature
                                                                ) &&
                                                                !getIsLoading()
                                                                    ? executeAction(
                                                                          feature.id
                                                                      )
                                                                    : null
                                                            ">
                                                            <!-- Feature Info -->
                                                            <div
                                                                class="tw-flex tw-items-center tw-max-w-[75%]">
                                                                <div
                                                                    class="tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-full tw-bg-gray-100 tw-mr-3">
                                                                    <mat-icon
                                                                        fontSet="material-symbols-outlined"
                                                                        class="tw-text-gray-600 tw-text-lg"
                                                                        >{{
                                                                            feature.icon
                                                                        }}</mat-icon
                                                                    >
                                                                </div>
                                                                <div
                                                                    class="tw-overflow-hidden">
                                                                    <div
                                                                        class="tw-text-sm tw-font-medium tw-text-gray-800 tw-truncate">
                                                                        {{
                                                                            feature.name
                                                                        }}
                                                                    </div>
                                                                    <div
                                                                        class="tw-text-xs tw-text-gray-500 tw-truncate">
                                                                        {{
                                                                            feature.description
                                                                        }}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <!-- Toggle switch ONLY for Toggle features -->
                                                            @if (
                                                                isToggleFeature(
                                                                    feature
                                                                )
                                                            ) {
                                                                <mat-slide-toggle
                                                                    [checked]="
                                                                        feature.enabled
                                                                    "
                                                                    [disabled]="
                                                                        getIsLoading()
                                                                    "
                                                                    (change)="
                                                                        toggleFeature(
                                                                            feature.id
                                                                        )
                                                                    "
                                                                    (click)="
                                                                        $event.stopPropagation()
                                                                    "
                                                                    [color]="
                                                                        feature.enabled
                                                                            ? 'accent'
                                                                            : 'primary'
                                                                    ">
                                                                </mat-slide-toggle>
                                                            }
                                                        </div>
                                                    }
                                                </div>
                                            }
                                        }
                                    </div>
                                </mat-tab>
                            }
                        </mat-tab-group>
                    </div>
                }
                <!-- Search results -->
                @if (searchQuery()) {
                    <div
                        class="tw-max-h-[320px] tw-overflow-y-auto tw-bg-white">
                        @for (
                            category of getFeatureCategories();
                            track trackByCategory($index, category)
                        ) {
                            <!-- Skip empty categories -->
                            @if (category.features.length > 0) {
                                <!-- Category header -->
                                <div
                                    class="tw-px-4 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-bg-gray-50 tw-sticky tw-top-0 tw-z-10">
                                    {{ category.name }}
                                </div>
                                <div class="tw-divide-y tw-divide-gray-100">
                                    <!-- Use trackBy for performance -->
                                    @for (
                                        feature of category.features;
                                        track trackByFeatureId($index, feature)
                                    ) {
                                        <div
                                            class="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 hover:tw-bg-gray-50"
                                            [class.tw-cursor-pointer]="
                                                isActionFeature(feature) &&
                                                !getIsLoading()
                                            "
                                            [class.tw-cursor-default]="
                                                isToggleFeature(feature) ||
                                                getIsLoading()
                                            "
                                            [class.tw-opacity-50]="
                                                getIsLoading()
                                            "
                                            (click)="
                                                isActionFeature(feature) &&
                                                !getIsLoading()
                                                    ? executeAction(feature.id)
                                                    : null
                                            ">
                                            <!-- Feature Info -->
                                            <div
                                                class="tw-flex tw-items-center tw-max-w-[75%]">
                                                <div
                                                    class="tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-full tw-bg-gray-100 tw-mr-3">
                                                    <mat-icon
                                                        fontSet="material-symbols-outlined"
                                                        class="tw-text-gray-600 tw-text-lg"
                                                        >{{
                                                            feature.icon
                                                        }}</mat-icon
                                                    >
                                                </div>
                                                <div class="tw-overflow-hidden">
                                                    <div
                                                        class="tw-text-sm tw-font-medium tw-text-gray-800 tw-truncate">
                                                        {{ feature.name }}
                                                    </div>
                                                    <div
                                                        class="tw-text-xs tw-text-gray-500 tw-truncate">
                                                        {{
                                                            feature.description
                                                        }}
                                                    </div>
                                                </div>
                                            </div>
                                            <!-- Toggle switch ONLY for Toggle features -->
                                            @if (isToggleFeature(feature)) {
                                                <mat-slide-toggle
                                                    [checked]="feature.enabled"
                                                    [disabled]="getIsLoading()"
                                                    (change)="
                                                        toggleFeature(
                                                            feature.id
                                                        )
                                                    "
                                                    (click)="
                                                        $event.stopPropagation()
                                                    "
                                                    [color]="
                                                        feature.enabled
                                                            ? 'accent'
                                                            : 'primary'
                                                    ">
                                                </mat-slide-toggle>
                                            }
                                        </div>
                                    }
                                </div>
                            }
                        }
                        <!-- Empty state for search -->
                        @if (filteredFeatures().length === 0) {
                            <div class="tw-p-6 tw-text-center tw-text-gray-500">
                                <mat-icon
                                    fontSet="material-symbols-outlined"
                                    class="tw-text-gray-400 tw-text-4xl tw-mb-2"
                                    >search_off</mat-icon
                                >
                                <p class="tw-text-sm">
                                    No matching commands found
                                </p>
                            </div>
                        }
                    </div>
                }
            </div>
        }
        <!-- Resize handle -->
        <div
            class="tw-absolute tw-bottom-0 tw-right-0 tw-w-4 tw-h-4 tw-z-10"
            [ngClass]="{
                'tw-cursor-nwse-resize': !getIsLoading(),
                'tw-cursor-not-allowed': getIsLoading(),
            }"
            (mousedown)="!getIsLoading() && onMouseDown($event, 'resize')">
            <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                class="tw-absolute tw-bottom-1 tw-right-1 tw-text-gray-400">
                <path
                    d="M0 10L10 0V10H0Z"
                    fill="currentColor"
                    opacity="0.4" />
            </svg>
        </div>
    </div>
}
